import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Menu, X, UserPlus } from "lucide-react";

const links = [
  { label: "Network", href: "#creators" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Campaigns", href: "#campaigns" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2 md:py-3" : "py-3 md:py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-10">
        <div
          className={`flex items-center justify-between rounded-2xl px-4 md:px-8 py-2.5 md:py-4 transition-all duration-500 ${
            scrolled
              ? "glass shadow-[0_10px_40px_-15px_rgba(15,23,42,0.15)] dark:shadow-[0_10px_40px_-15px_rgba(0,0,0,0.5)]"
              : "bg-transparent"
          }`}
        >
          <a
            href="#top"
            data-testid="logo-affix"
            className="font-heading font-extrabold text-xl md:text-[28px] tracking-tight text-slate-900 dark:text-white"
          >
            affix
            <span className="inline-block w-1.5 h-1.5 bg-[#2563EB] rounded-full ml-0.5 align-baseline anim-pulse-glow" />
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-body text-[15px] font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#2563EB] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to="/join-creator"
              data-testid="navbar-join-creator"
              className="hidden sm:inline-flex items-center gap-2 rounded-full px-4 md:px-5 py-2.5 md:py-3 text-[13px] md:text-[14px] font-semibold bg-white dark:bg-white/5 border border-slate-200 dark:border-white/15 text-slate-800 dark:text-white hover:border-[#2563EB] hover:text-[#2563EB] dark:hover:text-blue-300 transition-all duration-300 font-body"
            >
              <UserPlus className="w-4 h-4" />
              <span className="hidden md:inline">Join as Creator</span>
              <span className="md:hidden">Join</span>
            </Link>
            <a
              href="#contact"
              data-testid="navbar-cta"
              className="group hidden sm:inline-flex items-center gap-2 rounded-full px-4 md:px-6 py-2.5 md:py-3 text-[13px] md:text-[15px] font-medium bg-[#2563EB] text-white hover:bg-[#1E3A8A] transition-colors duration-300 font-body"
            >
              <span className="hidden md:inline">Book a Strategy Call</span>
              <span className="md:hidden">Book Call</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            {/* Hamburger (mobile + tablet) */}
            <button
              type="button"
              aria-label="Toggle menu"
              data-testid="navbar-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 rounded-full bg-white dark:bg-white/10 border border-slate-200 dark:border-white/15 flex items-center justify-center text-slate-800 dark:text-white"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            data-testid="navbar-mobile-panel"
            className="lg:hidden mx-4 mt-2 rounded-2xl glass p-4 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.2)] dark:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)]"
          >
            <nav className="flex flex-col">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-body text-[15px] font-medium text-slate-800 dark:text-white py-3 border-b border-slate-200/60 dark:border-white/10 last:border-b-0"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/join-creator"
                onClick={() => setOpen(false)}
                className="sm:hidden mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-white border border-slate-200 text-slate-900 px-5 py-3 text-[14px] font-semibold font-body"
              >
                <UserPlus className="w-4 h-4" /> Join as Creator
              </Link>
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="sm:hidden mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] text-white px-5 py-3 text-[15px] font-semibold font-body"
              >
                Book a Strategy Call <ArrowRight className="w-4 h-4" />
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
